import styled, { css } from 'styled-components';

import {
    BACKGROUND_DEEP,
    BUTTON_CONTENT_BASE,
    CONTENT_BASE,
    CONTENT_DISABLED,
    PRIMARY_BASE,
    PRIMARY_DISABLED,
    PRIMARY_HOVER,
    PRIMARY_MUTED,
    PRIMARY_PRESS,
    TERTIARY_BACKGROUND,
    TERTIARY_BORDER
} from '@amzn/ring-common-style-library/lib/colors';

import {
    BODY_NORMAL_MEDIUM,
    BODY_SMALL_MEDIUM,
    BODY_LARGE_MEDIUM
} from '@amzn/ring-common-style-library/lib/typography';

const fontStyles = {
    small: css`
        ${BODY_SMALL_MEDIUM};
    `,
    normal: css`
        ${BODY_NORMAL_MEDIUM}
    `,
    large: css`
        ${BODY_LARGE_MEDIUM};
    `,
};

const buttonStates = css<{
    disabled?: boolean;
    selected?: boolean;
    variant?: 'button' | 'checkbox' | 'radio';
}>`
    border: 1px solid ${TERTIARY_BORDER};
    background: ${BUTTON_CONTENT_BASE};
    color: ${CONTENT_BASE};

    ${({ disabled }) =>
        !disabled &&
        css`
            cursor: pointer;
            &:hover {
                background: ${BUTTON_CONTENT_BASE};
                border: 1px solid ${PRIMARY_HOVER};
                color: ${PRIMARY_HOVER};
            }

            &:active {
                background: ${PRIMARY_PRESS};
                border: 1px solid ${PRIMARY_BASE};
                color: ${PRIMARY_MUTED};
            }
        `}

    ${({ disabled }) =>
        disabled &&
        css`
            background: ${TERTIARY_BACKGROUND};
            border: 1px solid ${CONTENT_DISABLED};
            color: ${CONTENT_DISABLED};
            cursor: not-allowed;
        `}

    ${({ selected }) =>
        selected &&
        css<{ variant?: 'button' | 'checkbox' | 'radio' }>`
            background: ${({ variant }) =>
                variant === 'button' ? BACKGROUND_DEEP : PRIMARY_PRESS};
            border: ${({ variant }) =>
                variant === 'button'
                    ? `1px solid ${PRIMARY_DISABLED}`
                    : `1px solid ${PRIMARY_BASE}`};
            color: ${({ variant }) =>
                variant === 'button' ? PRIMARY_PRESS : PRIMARY_MUTED};
        `}

    ${({ selected, disabled }) =>
        selected &&
        disabled &&
        css<{ variant?: 'button' | 'checkbox' | 'radio' }>`
            background: ${({ variant }) =>
                variant === 'button' ? BACKGROUND_DEEP : PRIMARY_PRESS};
            border: ${({ variant }) =>
                variant === 'button'
                    ? `1px solid ${PRIMARY_DISABLED}`
                    : `1px solid ${PRIMARY_BASE}`};
            color: ${({ variant }) =>
                variant === 'button' ? PRIMARY_PRESS : PRIMARY_MUTED};
        `}
`;

const Checkbox = styled.div<{
    disabled?: boolean;
    selected?: boolean;
}>`
    height: 24px;
    width: 24px;

    display: inline-flex;
    justify-content: center;
    align-items: center;
    text-align: left;
    margin: 0px;

    border-radius: 4px;
    ${buttonStates}
    border: ${({ selected }) => (selected && 'none')};
`;

const Radio = styled.div<{
    disabled?: boolean;
    selected?: boolean;
}>`
    height: 24px;
    width: 24px;

    display: inline-flex;
    justify-content: center;
    align-items: center;
    text-align: left;
    margin: 0px;

    border-radius: 50px;
    ${buttonStates}
    border: ${({ selected }) => (selected && 'none')};
`;

const Button = styled.button<{
    size: 'small' | 'normal' | 'large';
    disabled?: boolean;
    selected?: boolean;
    stretch?: boolean;
    variant?: 'button' | 'checkbox' | 'radio';
}>`
    display: inline-flex;
    padding: 8px 12px;
    align-items: center;
    justify-content: ${({ variant }) => (variant === 'button' ? 'center' : 'flex-start')};
    text-align: left;
    margin: 0px;
    gap: 8px;

    border-radius: 8px;
    ${buttonStates}

    ${({ size }) => fontStyles[size]}
    ${({ stretch }) => stretch && css`
        width: 100%;
    `}

    &:hover ${Checkbox},
    &:hover ${Radio} {
        ${({ disabled, selected }) => {
        if (disabled && !selected) {
            // When disabled and not selected, do not change styling.
            return css``;
        } else if (disabled && selected) {
            // When disabled AND selected, force hover styling to PRIMARY_PRESS.
            return css`
                background: ${PRIMARY_PRESS};
                border: none;
                color: ${PRIMARY_MUTED};
            `;
        } else if (selected && !disabled) {
            // When selected (and not disabled), use alternate hover styling.
            return css`
                background: ${BUTTON_CONTENT_BASE};
                border: none;
                color: ${PRIMARY_HOVER};
            `;
        } else {
            // Default hover styling if not selected.
            return css`
                background: ${BUTTON_CONTENT_BASE};
                border: 1px solid ${PRIMARY_HOVER};
                color: ${PRIMARY_HOVER};
            `;
        }
        }}
    }
      &:active ${Checkbox},
    &:active ${Radio} {
        ${({ disabled, selected }) => {
        if (disabled && !selected) {
            // When disabled and not selected, maintain the disabled styling.
            return css``;
        } else if (disabled && selected) {
            // When disabled and selected, force background to PRIMARY_PRESS.
            return css`
                background: ${PRIMARY_PRESS};
                border: none;
                color: ${PRIMARY_MUTED};
            `;
        } else if (selected && !disabled) {
            return css`
                background: ${PRIMARY_PRESS};
                border: none;
                color: ${PRIMARY_MUTED};
            `;
        } else {
            return css`
                background: ${PRIMARY_PRESS};
                border: 1px solid ${PRIMARY_BASE};
                color: ${PRIMARY_MUTED};
            `;
        }
        }}
    }
`;
export default {
    Button,
    Checkbox,
    Radio,
}